import React from 'react';
import { Trash2 } from 'lucide-react';
import { GlassButton } from '../ui';
import type { ImportedSection } from '../../services/api';
import { HOST_CLONE_ID, cloneTagOf, cloneTagLabel, cloneTextPreview, deleteConfirmText } from './ImportedSectionList';

/**
 * Right-pane body when the selected section is a copied host block (`host_clone`). A copy has no
 * properties schema, no variants and no link anchor: its text is edited on the page itself (the edit
 * script saves it through PATCH /clones/{id}/text), so this panel only explains that and offers Delete.
 * Inline styles + CSS variables only.
 */
interface Props {
  section: ImportedSection;
  onDelete: (sectionId: string) => void;
}

const ImportedClonePanel: React.FC<Props> = ({ section, onDelete }) => {
  const html = section.controls?.html;
  const preview = cloneTextPreview(html, 160);
  return (
    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Copied block</div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{cloneTagLabel(cloneTagOf(html))}</div>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        A copy of a block from this page, using the page&rsquo;s own styling. Click its text on the page to
        change it &mdash; text changes save automatically.
      </div>
      <div style={{
        fontSize: 12, color: 'var(--text)', background: 'var(--panel-strong)', border: '1px solid var(--stroke)',
        borderRadius: 'var(--radius-md)', padding: '8px 10px', wordBreak: 'break-word',
      }}>
        {preview || '(empty)'}
      </div>
      <GlassButton
        variant="danger"
        onClick={() => {
          if (window.confirm(deleteConfirmText('Copied block', HOST_CLONE_ID))) onDelete(section.section_id);
        }}
      >
        <Trash2 size={14} /> Delete copy
      </GlassButton>
    </div>
  );
};

export default ImportedClonePanel;
